import {
  emptyLeaderboardProfile,
  mergePendingBattleWins,
  type BattleLeaderboardProfile,
  type BattleWinEvent,
} from 'features/battle/leaderboard';

export type { BattleWinEvent };

const url = '/.netlify/functions/battle-leaderboard';
const POST_ATTEMPTS = 3;

let writeTail = Promise.resolve();
const pending = new Map<string, BattleWinEvent>();

const hydrateProfile = (row: BattleLeaderboardProfile): BattleLeaderboardProfile => {
  const base = emptyLeaderboardProfile(row.profileId, row.name);
  return {
    ...base,
    ...row,
    name: typeof row.name === 'string' && row.name.trim() ? row.name.trim() : base.name,
    updatedAt: Number(row.updatedAt) || 0,
    classic: {
      easy: { ...base.classic.easy, ...row.classic?.easy },
      hard: { ...base.classic.hard, ...row.classic?.hard },
      impossible: { ...base.classic.impossible, ...row.classic?.impossible },
    },
    arena: {
      easy: { ...base.arena.easy, ...row.arena?.easy },
      hard: { ...base.arena.hard, ...row.arena?.hard },
      impossible: { ...base.arena.impossible, ...row.arena?.impossible },
    },
    recentGameIds: Array.isArray(row.recentGameIds) ? row.recentGameIds.filter((id) => typeof id === 'string') : [],
  };
};

export const recordBattleWin = (event: BattleWinEvent): void => {
  pending.set(event.gameId, event);
  writeTail = writeTail
    .then(async () => {
      for (let attempt = 0; attempt < POST_ATTEMPTS; attempt += 1) {
        try {
          const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(event),
            keepalive: true,
          });
          if (response.ok) return;
        } catch {
          /* retry */
        }
      }
    })
    .catch(() => undefined);
};

export const fetchBattleLeaderboard = async (): Promise<BattleLeaderboardProfile[]> => {
  await writeTail;
  const response = await fetch(`${url}?t=${Date.now()}`, { cache: 'no-store' });
  if (!response.ok) throw new Error('Failed to load battle leaderboard');
  const body = (await response.json()) as { profiles?: BattleLeaderboardProfile[] };
  const rows = (Array.isArray(body.profiles) ? body.profiles : []).map(hydrateProfile);
  for (const event of [...pending.values()]) {
    const profile = rows.find((row) => row.profileId === event.profileId);
    if (profile?.recentGameIds.includes(event.gameId)) pending.delete(event.gameId);
  }
  return mergePendingBattleWins(rows, [...pending.values()]);
};
